/**
 * Pipedrive People Page
 * List of people with search, filtering, and pagination
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { usePipedrivePeople } from '@/hooks/use-pipedrive-api'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Users, Search, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import type { PipedrivePerson } from '@/types/pipedrive'

export default function PipedrivePeople() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const limit = 25

  const { data, isLoading, error } = usePipedrivePeople({
    page,
    limit,
    search,
  })

  const people = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.pages ?? 1

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">People</h1>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load people. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" aria-hidden="true" />
            People
          </h1>
          <p className="text-muted-foreground">{total.toLocaleString()} people</p>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search people..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9"
                  aria-label="Search people"
                />
              </div>
            </div>
            {search && (
              <Button variant="outline" onClick={() => handleSearch('')}>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Job Title</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Open Deals</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-36" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-12 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : people.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No people found
                  </TableCell>
                </TableRow>
              ) : (
                people.map((person: PipedrivePerson) => (
                  <TableRow key={person.id}>
                    <TableCell>
                      <Link
                        to={`/pipedrive/people/${person.id}`}
                        className="font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        {person.name}
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    </TableCell>
                    <TableCell>
                      {person.organization_id ? (
                        <Link
                          to={`/pipedrive/organizations/${person.organization_id}`}
                          className="text-primary hover:underline"
                        >
                          {person.organization || '-'}
                        </Link>
                      ) : (
                        person.organization || '-'
                      )}
                    </TableCell>
                    <TableCell>{person.job_title || '-'}</TableCell>
                    <TableCell>{person.email_work || '-'}</TableCell>
                    <TableCell>{person.phone_work || person.phone_mobile || '-'}</TableCell>
                    <TableCell className="text-right">{person.open_deals_count ?? 0}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
            {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
